import { For, Show, createResource, createSignal } from 'solid-js';
import { api, Partner } from '../api';
import { isOperator } from '../me';
import { ConfirmButton, ErrorLine, PageHeader, StatusPill, useAction } from '../ui';
import Users from './Users';

export default function Partners() {
  const [partners, { refetch }] = createResource(() => api<Partner[]>('/partners'));
  const [slug, setSlug] = createSignal('');
  const [name, setName] = createSignal('');
  const [open, setOpen] = createSignal<{ id: string; pane: 'admins' | 'settings' } | null>(null);
  const toggle = (id: string, pane: 'admins' | 'settings') => setOpen(open()?.id === id && open()?.pane === pane ? null : { id, pane });
  const a = useAction();

  const create = (e: Event) => {
    e.preventDefault();
    a.run(async () => {
      await api('/partners', { method: 'POST', json: { slug: slug(), name: name() } });
      setSlug(''); setName(''); refetch();
    });
  };

  return (
    <>
      <PageHeader title="Partners" />
      <Show when={isOperator()}>
        <form class="card-z mb-6 flex flex-wrap items-end gap-3" onSubmit={create}>
          <label class="form-control"><span class="label-text font-semibold">Name</span>
            <input class="input input-bordered" required value={name()} onInput={(e) => setName(e.currentTarget.value)} /></label>
          <label class="form-control"><span class="label-text font-semibold">Slug</span>
            <input class="input input-bordered mono" required pattern="[a-z0-9][a-z0-9-]{1,63}" value={slug()}
              onInput={(e) => setSlug(e.currentTarget.value.toLowerCase())} /></label>
          <button class="btn btn-primary" disabled={a.busy()}>New partner</button>
          <ErrorLine error={a.error} />
        </form>
      </Show>
      <div class="card-z overflow-x-auto p-0">
        <table class="table">
          <thead><tr><th class="th">Partner</th><th class="th">Slug</th><th class="th">Status</th><th /></tr></thead>
          <tbody>
            <For each={partners()}>
              {(p) => (
                <>
                  <tr>
                    <td class="font-semibold">{p.name}</td>
                    <td class="mono text-ink-3">{p.slug}</td>
                    <td><StatusPill tone={p.status === 'active' ? 'ok' : 'off'}>{p.status}</StatusPill></td>
                    <td class="whitespace-nowrap text-right">
                      <button class="btn btn-ghost btn-sm" aria-expanded={open()?.id === p.id && open()?.pane === 'settings'}
                        onClick={() => toggle(p.id, 'settings')}>Settings</button>
                      <button class="btn btn-ghost btn-sm" aria-expanded={open()?.id === p.id && open()?.pane === 'admins'}
                        onClick={() => toggle(p.id, 'admins')}>Admins</button>
                    </td>
                  </tr>
                  <Show when={open()?.id === p.id}>
                    <tr><td colspan="4" class="bg-static">
                      <Show when={open()?.pane === 'admins'} fallback={<PartnerSettings partner={p} onSaved={refetch} />}>
                        <Users scope={{ partner_id: p.id }} roles={['partner_admin']} />
                      </Show>
                    </td></tr>
                  </Show>
                </>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </>
  );
}

/** Name and status are the operator's; branding (logo, lamp accent, display title on the kiosk) the partner's too. */
function PartnerSettings(props: { partner: Partner; onSaved: () => void }) {
  const [name, setName] = createSignal(props.partner.name);
  const [title, setTitle] = createSignal(props.partner.branding?.title ?? '');
  const [logo, setLogo] = createSignal(props.partner.branding?.logo ?? '');
  const [accent, setAccent] = createSignal(props.partner.branding?.lamp_accent ?? '');
  const a = useAction();
  const save = (json: any) => a.run(async () => { await api(`/partners/${props.partner.id}`, { method: 'PATCH', json }); props.onSaved(); });
  const saveBranding = (e: Event) => {
    e.preventDefault();
    const b = { title: title() || undefined, logo: logo() || undefined, lamp_accent: accent() || undefined };
    save({ branding: Object.values(b).some(Boolean) ? b : null });
  };

  return (
    <div class="flex flex-col gap-5 py-2">
      <Show when={isOperator()}>
        <form class="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); save({ name: name() }); }}>
          <label class="form-control"><span class="label-text font-semibold">Name</span>
            <input class="input input-sm input-bordered" required value={name()} onInput={(e) => setName(e.currentTarget.value)} /></label>
          <button class="btn btn-sm btn-primary" disabled={a.busy() || name() === props.partner.name}>Rename</button>
        </form>
      </Show>
      <form class="flex flex-wrap items-end gap-3" onSubmit={saveBranding}>
        <label class="form-control"><span class="label-text font-semibold">Kiosk title</span>
          <input class="input input-sm input-bordered" maxlength="60" placeholder="scai.fm Zones" value={title()} onInput={(e) => setTitle(e.currentTarget.value)} /></label>
        <label class="form-control w-80"><span class="label-text font-semibold">Logo URL (https)</span>
          <input class="input input-sm input-bordered" type="url" pattern="https://.*" placeholder="https://…/logo.png" value={logo()} onInput={(e) => setLogo(e.currentTarget.value)} /></label>
        <label class="form-control"><span class="label-text font-semibold">Lamp accent</span>
          <div class="flex items-center gap-2">
            <input type="color" class="h-8 w-10 cursor-pointer rounded border border-line" value={accent() || '#3ddc84'} aria-label="Lamp accent colour"
              onInput={(e) => setAccent(e.currentTarget.value)} />
            <Show when={accent()}><button type="button" class="btn btn-ghost btn-xs" onClick={() => setAccent('')}>Default</button></Show>
          </div></label>
        <button class="btn btn-sm btn-primary" disabled={a.busy()}>Save branding</button>
      </form>
      <Show when={isOperator()}>
        <BillingSettings partner={props.partner} busy={a.busy()} save={save} />
      </Show>
      <Show when={isOperator()}>
        <div>
          <Show when={props.partner.status === 'active'} fallback={
            <button class="btn btn-sm btn-secondary" disabled={a.busy()} onClick={() => save({ status: 'active' })}>Reactivate partner</button>}>
            <ConfirmButton label="Suspend partner" confirm="Suspend" busy={a.busy()} onConfirm={() => save({ status: 'suspended' })}
              warning={`Every zone of every ${props.partner.name} customer goes silent within about 20 seconds, until you reactivate the partner.`} />
          </Show>
        </div>
      </Show>
      <ErrorLine error={a.error} />
    </div>
  );
}

/** How ScaiLabs bills this partner's customers through ScaiControl (operators only). */
function BillingSettings(props: { partner: Partner; busy: boolean; save: (json: any) => void }) {
  const [mode, setMode] = createSignal(props.partner.billing_mode);
  const [ref, setRef] = createSignal(props.partner.scaicontrol_partner_id ?? '');
  const dirty = () => mode() !== props.partner.billing_mode || ref() !== (props.partner.scaicontrol_partner_id ?? '');
  return (
    <form class="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); props.save({ billing_mode: mode(), scaicontrol_partner_id: ref() || null }); }}>
      <label class="form-control"><span class="label-text font-semibold">Billing</span>
        <select class="select select-sm select-bordered" value={mode()} onChange={(e) => setMode(e.currentTarget.value as any)}>
          <option value="customer">Customer mode: ScaiLabs invoices each customer, partner earns commission</option>
          <option value="partner">Partner mode: ScaiLabs invoices the partner at a discount</option>
        </select></label>
      <label class="form-control"><span class="label-text font-semibold">ScaiControl partner id</span>
        <input class="input input-sm input-bordered mono w-48" placeholder="prt_…" value={ref()} onInput={(e) => setRef(e.currentTarget.value.trim())} /></label>
      <button class="btn btn-sm btn-primary" disabled={props.busy || !dirty()}>Save billing</button>
    </form>
  );
}
